import React, { useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import KeyboardArrowDownRoundedIcon from '@mui/icons-material/KeyboardArrowDownRounded';
import KeyboardArrowUpRoundedIcon from '@mui/icons-material/KeyboardArrowUpRounded';
import { TertiaryButton } from 'v3/Components/Shared/Buttons';

export type ColumnConfig<T> = {
  header: string;
  accessor: keyof T & string;
  width?: number;
  transform?: (value: T[keyof T]) => string;
};

type Props<T extends { id: string }> = {
  title: string;
  columns: ColumnConfig<T>[];
  data: T[];
  hiddenData?: T[];
  addLabel: string;
  onAdd?: () => void;
  onUpdate?: (item: T) => void;
  showHiddenLabel?: string;
  emptyMessage?: string;
};

export function ItemConfigurationTable<T extends { id: string }>({
  title,
  columns,
  data,
  hiddenData,
  addLabel,
  onAdd,
  onUpdate,
  showHiddenLabel,
  emptyMessage = 'No items added',
}: Props<T>) {
  const [showHidden, setShowHidden] = useState(false);

  const renderRow = (row: T, hidden = false) => (
    <tr key={row.id} className={hidden ? 'overview-license-table__hidden-row' : undefined}>
      {columns.map((col, i) => (
        <td key={i}>{col.transform ? col.transform(row[col.accessor]) : (row[col.accessor] as string) ?? '—'}</td>
      ))}
      <td>
        {!hidden && onUpdate && (
          <button
            className="overview-license-table__update-link"
            onClick={() => onUpdate(row)}
          >
            Update
          </button>
        )}
      </td>
    </tr>
  );

  return (
    <>
      <div className="overview-license-section">
        <div className="overview-license-section__header">
          <span>{title}</span>
          {onAdd && (
            <TertiaryButton iconPosition="start" title={addLabel} size="compact" onClick={onAdd}>
              <AddIcon />
            </TertiaryButton>
          )}
        </div>
        <div className="overview-license-table">
          <table>
            <colgroup>
              {columns.map((col, i) => (
                <col key={i} style={col.width ? { width: col.width } : undefined} />
              ))}
              <col style={{ width: 100 }} />
            </colgroup>
            <thead>
              <tr>
                {columns.map((col, i) => <th key={i}>{col.header}</th>)}
                <th />
              </tr>
            </thead>
            <tbody>
              {data.length === 0 && (!showHidden || !hiddenData?.length) ? (
                <tr>
                  <td colSpan={columns.length + 1} style={{ color: '#546778', fontStyle: 'italic' }}>
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                <>
                  {data.map((row) => renderRow(row))}
                  {showHidden && hiddenData?.map((row) => renderRow(row, true))}
                </>
              )}
            </tbody>
          </table>
        </div>
      </div>
      {showHiddenLabel && !!hiddenData?.length && (
        <div className="license-active__toggle-row" onClick={() => setShowHidden(!showHidden)}>
          {showHidden ? showHiddenLabel.replace(/^Show/, 'Hide') : showHiddenLabel}
          {showHidden ? <KeyboardArrowUpRoundedIcon /> : <KeyboardArrowDownRoundedIcon />}
        </div>
      )}
    </>
  );
}
